import { getLocale, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarBlank,
  Heart,
} from "@phosphor-icons/react";
import AuthenticatedImage from "../../shared/AuthenticatedImage.tsx";
import AppFeedback from "../../shared/AppFeedback.tsx";
import LoadingRing, { PendingRing } from "../../shared/LoadingRing.jsx";
import { AccountEmptyState } from "../../shared/account/AccountEmptyState.tsx";
import {
  UserProductFavorite,
  getFavorites,
  removeFavorite,
} from "./favoriteService.ts";
import "./UserProductFavorites.css";
import { isAgencyShopSession } from "../Orders/shopSession.ts";
import { buildVariantPath } from "../productUrl.ts";
import { formatShamsiDate } from "../../../utils/formatShamsiDateTime.ts";
import { agencySessionKey, subscribeAgencySession } from "../../../utils/agencySession.ts";

function UserProductFavorites() {
  const storeLanguage = useStoreLanguage();
  const session = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<UserProductFavorite[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const removalLocks = useRef(new Set<number>());

  useEffect(
    function () {
      let active = true;
      setFavorites([]);
      if (!isAgencyShopSession()) {
        navigate("/login");
        return;
      }

      async function loadFavorites() {
        setLoading(true);
        try {
          const loaded = await getFavorites();
          if (active && session === agencySessionKey()) setFavorites(loaded);
        } catch (error) {
          if (active && session === agencySessionKey()) await AppFeedback.httpError(error);
        } finally {
          if (active) setLoading(false);
        }
      }

      void loadFavorites();
      return function () { active = false; };
    },
    [storeLanguage, navigate, session],
  );

  async function handleRemove(productVariantId: number) {
    if (!isAgencyShopSession() || removingIds.has(productVariantId) || removalLocks.current.has(productVariantId)) return;
    const requestedSession = agencySessionKey();
    removalLocks.current.add(productVariantId);
    setRemovingIds(function (currentIds) {
      return new Set(currentIds).add(productVariantId);
    });
    try {
      await removeFavorite(productVariantId);
      if (requestedSession !== agencySessionKey()) return;
      setFavorites(function (currentFavorites) {
        return currentFavorites.filter(function (favorite) {
          return favorite.productVariantId !== productVariantId;
        });
      });
      AppFeedback.success({ title: t("از علاقه‌مندی‌ها حذف شد.") });
    } catch (error: unknown) {
      await AppFeedback.httpError(error);
    } finally {
      removalLocks.current.delete(productVariantId);
      setRemovingIds(function (currentIds) {
        const nextIds = new Set(currentIds);
        nextIds.delete(productVariantId);
        return nextIds;
      });
    }
  }

  return (
    <div className="user-product-favorites-page">
      {loading ? (
        <div className="user-product-favorites-loading">
          <LoadingRing tip={t("در حال آماده‌سازی علاقه‌مندی‌ها…")} />
        </div>
      ) : favorites.length === 0 ? (
        <AccountEmptyState
          pageTitle={t("علاقه‌مندی‌ها")}
          icon={<Heart weight="duotone" />}
          title={t("جای طرح‌های مورد علاقهٔ شما اینجاست")}
          actionLabel={t("دیدن طرح‌های کاتالوگ")}
          actionTo="/products"
          hint={t("در کاتالوگ روی قلب هر طرح بزنید تا بعداً سریع و راحت به آن برگردید.")}
        />
      ) : (
        <section className="user-product-favorites-grid" aria-label={t("طرح‌های مورد علاقه")}>
          {favorites.map(function (favorite) {
            const isRemoving = removingIds.has(favorite.productVariantId);
            const title = favorite.isVisible ? favorite.title : t("این کالا دیگر قابل نمایش نیست");
            return (
              <article
                key={favorite.productVariantId}
                className="user-product-favorite-card"
              >
                <div className="user-product-favorite-card__media">
                  <AuthenticatedImage
                    fileId={favorite.fileId}
                    disablePreview={true}
                    alt={title}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                  <button
                    type="button"
                    className="user-product-favorite-card__remove"
                    disabled={isRemoving || !isAgencyShopSession()}
                    aria-busy={isRemoving}
                    onClick={function (event) {
                      event.stopPropagation();
                      void handleRemove(favorite.productVariantId);
                    }}
                    onKeyDown={function (event) {
                      event.stopPropagation();
                    }}
                    aria-label={t("حذف {{value0}} از علاقه‌مندی‌ها", { value0: title })}
                    title={t("حذف از علاقه‌مندی‌ها")}
                  >
                    {isRemoving ? <PendingRing /> : <Heart size={22} weight="fill" />}
                  </button>
                </div>
                <div className="user-product-favorite-card__body">
                  <h2>
                    {favorite.isVisible ? (
                      <Link
                        className="user-product-favorite-card__link"
                        to={buildVariantPath(favorite.slug, favorite.variantAddressTitle)}
                        aria-label={t("مشاهده جزئیات {{value0}}", { value0: title })}
                      >
                        {title}
                      </Link>
                    ) : title}
                  </h2>
                  <time dateTime={favorite.addedAt}>
                    <CalendarBlank size={18} weight="duotone" />
                    {t("تاریخ افزودن: {{value0}}", {
                      value0: formatShamsiDate(favorite.addedAt, getLocale()),
                    })}
                  </time>
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

export default UserProductFavorites;
